import camelCase from 'lodash/camelCase';

import { getRequest } from './api';

export const BANNER_PAGES = ['landing', 'lobby_sc', 'lobby_cc', 'lobby_animation_only'] as const;
export type BannerPage = (typeof BANNER_PAGES)[number];

export type Banner = {
  desktop: string;
  mobile?: string | null;
  desktopBanner: {
    imageUrl: string;
    animationUrl?: string;
    animationBackgroundUrl?: string;
  };
  mobileBanner: {
    imageUrl?: string | null;
    animationUrl?: string;
    mobileAnimationBackgroundUrl?: string;
  };
};

type BannersRecord<B> = Partial<Record<BannerPage, B | null>>;
export type BannersData = BannersRecord<Banner>;

export type BannerCta = { type: string; value: string };
export type BannersResponseData = { banners: BannersData; cta?: BannerCta | null };

class UiService {
  private static UTM_PARAM_NAMES = ['utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term'];

  private static getUtmParams(): Record<string, string> {
    const searchParams = new URLSearchParams(window.location.search);

    return this.UTM_PARAM_NAMES.reduce<Record<string, string>>((result, paramName) => {
      const paramValue = searchParams.get(paramName);

      if (!paramValue) {
        return result;
      }

      return {
        ...result,
        [camelCase(paramName)]: paramValue,
      };
    }, {});
  }

  public async getBanners(options?: { lang?: string; theme?: string }) {
    const utmParams = UiService.getUtmParams();

    return getRequest<{ data: BannersResponseData }>('/v2/ui/banners', { ...options, ...utmParams });
  }
}

export const uiService = new UiService();

/**
 * The lobby banner <img> is the LCP element for most sessions, and its resourceLoadDelay is the
 * bulk of LCP (~6.2s at p75) — the image cannot start downloading until the banner URL is known,
 * and today that request is only issued by UiStore's constructor, i.e. after asyncWithLDProvider,
 * root.render() and the whole store graph. The response varies only on the profile (or, when
 * logged out, request-IP geo) plus lang, all of which are settled once initAuth resolves, so the
 * request can be issued from render.tsx and adopted by the store when it comes up.
 */
let primedBanners: { lang: string; request: ReturnType<UiService['getBanners']> } | null = null;

export const primeBanners = (lang: string) => {
  const request = uiService.getBanners({ lang });
  // Keep a handler attached: if the store never adopts this (CMS pages, an early render error),
  // a rejection here would otherwise surface as an unhandled rejection. The consumer still sees
  // the original rejection and does its own error handling.
  void request.catch(() => undefined);
  primedBanners = { lang, request };
  // Returned so the caller can warm the banner image off the same response without issuing a
  // second request; adopting it later via takePrimedBanners is unaffected.
  return request;
};

/** Returns the in-flight request only when it matches what the caller is asking for. */
export const takePrimedBanners = (options?: { lang?: string; theme?: string }) => {
  // A themed request is a different response entirely, and a language switch invalidates the prime.
  if (!primedBanners || options?.theme || options?.lang !== primedBanners.lang) {
    return null;
  }

  const { request } = primedBanners;
  primedBanners = null;
  return request;
};
